import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Header from './Header';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import { TokenState, EvaluatorOpinionDto } from '../api/fe-management-system-api/types';
import { tokenStateApi } from '../api/fe-management-system-api/token-state';
import { TokenStatus, OpinionItemType } from '../stores/data/types';
import { useToasterStore } from '../stores/ui/toasterStore';
import StatusChip from './StatusChip';
import CategoryChip from './CategoryChip';
import CopyButton from './CopyButton';
import RetrospectiveTypeChip from './RetrospectiveTypeChip';
import KeywordChip from './KeywordChip';
import KeywordEditor from './KeywordEditor';
import { useGlobalDeckFilterStore, allCategories, allStatuses } from '../stores/ui/globalDeckFilterStore';
import { useUIStore } from '../stores/ui/uiStore';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { formatTimestampToLocaleString } from '../utils/time';
import { useDataStore } from '../stores/data/dataStore';
import { formatNumber } from '../utils/formatters';

type SortField =
  | 'status'
  | 'category'
  | 'j_score'
  | 'h_score'
  | 's_score'
  | 'ns_total'
  | 'me_total'
  | 'aq_total'
  | 'r_total'
  | 'total'
  | 'latestTotal'
  | 'retrospective'
  | 'mc'
  | 'lafRank'
  | 'createdAt'
  | 'updatedAt';
type SortOrder = 'asc' | 'desc';

const NotesPage: React.FC = () => {
  const getTokenData = useDataStore((state) => state.getTokenData);
  const { showToaster } = useToasterStore();
  const { setSelectedToken, openNoteDetailModal } = useUIStore();
  const PAGE_LIMIT = 50;
  const [tokenStates, setTokenStates] = useState<TokenState[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [keywordSearchInput, setKeywordSearchInput] = useState('');
  const [appliedSearchKeyword, setAppliedSearchKeyword] = useState('');
  const [appliedKeywordSearchInput, setAppliedKeywordSearchInput] = useState('');
  const { selectedCategories, selectedStatuses } = useGlobalDeckFilterStore();
  const [hoveredMemoAddress, setHoveredMemoAddress] = useState<string | null>(null);
  const [editingMemoAddress, setEditingMemoAddress] = useState<string | null>(null);
  const [memoDraft, setMemoDraft] = useState('');
  const [isMemoSaving, setIsMemoSaving] = useState(false);
  const [hoveredKeywordAddress, setHoveredKeywordAddress] = useState<string | null>(null);
  const [editingKeywordAddress, setEditingKeywordAddress] = useState<string | null>(null);
  const [expandedSummaryAddress, setExpandedSummaryAddress] = useState<string | null>(null);
  const loadMoreSentinelRef = useRef<HTMLTableRowElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreParamsRef = useRef({ page: 0, hasMore: false, isLoadingMore: false, fetch: (_p: number, _a: boolean) => {} });
  const loadMoreInFlightRef = useRef(false);

  // 페이지네이션 API로 토큰 상태 목록 조회 (검색/키워드/카테고리/상태 필터 포함)
  const fetchTokenStatesPaginated = useCallback(
    async (pageNum: number, append: boolean) => {
      if (pageNum === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);
      try {
        const keywordsArr = appliedKeywordSearchInput.trim()
          ? appliedKeywordSearchInput.split(',').map((k) => k.trim()).filter(Boolean)
          : undefined;
        const { data, meta } = await feManagementSystemApi.getTokenStatesPaginated({
          page: pageNum,
          limit: PAGE_LIMIT,
          sortField,
          sortOrder,
          searchKeyword: appliedSearchKeyword.trim() || undefined,
          keywords: keywordsArr,
          category: selectedCategories.length === allCategories.length ? undefined : selectedCategories,
          status: selectedStatuses.length === allStatuses.length ? undefined : selectedStatuses,
        });
        setTokenStates((prev) => (append ? [...prev, ...data] : data));
        setPage(pageNum);
        setHasMore(meta.page < meta.totalPages);
      } catch (err: unknown) {
        console.error('토큰 상태 목록 조회 실패:', err);
        setError(
          err instanceof Error ? err.message : '토큰 상태 목록을 불러오는데 실패했습니다.'
        );
        showToaster(
          getApiErrorMessage(err, 'Failed to load token states.'),
          'error'
        );
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [sortField, sortOrder, appliedSearchKeyword, appliedKeywordSearchInput, selectedCategories, selectedStatuses, showToaster]
  );

  loadMoreParamsRef.current = { page, hasMore, isLoadingMore, fetch: fetchTokenStatesPaginated };
  if (!isLoadingMore) loadMoreInFlightRef.current = false;

  // 새로고침 시 1페이지부터 다시 로드
  const fetchTokenStates = useCallback(
    async (isRefreshing: boolean = false) => {
      await fetchTokenStatesPaginated(1, false);
    },
    [fetchTokenStatesPaginated]
  );

  const [isRestartingFetchingFailedTokens, setIsRestartingFetchingFailedTokens] = useState(false);

  const handleRestartFetchingFailedTokens = useCallback(async () => {
    if (isRestartingFetchingFailedTokens) return;

    setIsRestartingFetchingFailedTokens(true);
    try {
      await tokenStateApi.restartFetchingFailedTokens();
      await fetchTokenStates(true);
      showToaster('Restarted fetching failed tokens.', 'success');
    } catch (error: any) {
      showToaster(
        getApiErrorMessage(error, 'Failed to restart fetching failed tokens.'),
        'error',
      );
    } finally {
      setIsRestartingFetchingFailedTokens(false);
    }
  }, [fetchTokenStates, showToaster, isRestartingFetchingFailedTokens]);

  // Details 버튼 클릭 핸들러
  const handleDetailsClick = useCallback((tokenAddress: string, tokenSymbol?: string) => {
    setSelectedToken({
      tokenAddress,
      symbol: tokenSymbol || 'Unknown',
    });
    openNoteDetailModal();
  }, [setSelectedToken, openNoteDetailModal]);

  // 초기 로드 및 정렬 변경 시 1페이지부터 다시 로드
  useEffect(() => {
    fetchTokenStatesPaginated(1, false);
  }, [fetchTokenStatesPaginated]);

  // Search 버튼 또는 Enter 시에만 검색 적용 (입력값을 applied로 반영 후, useEffect에서 1페이지 재조회)
  const handleApplySearch = useCallback(() => {
    setAppliedSearchKeyword(searchKeyword);
    setAppliedKeywordSearchInput(keywordSearchInput);
  }, [searchKeyword, keywordSearchInput]);

  type ScoreMap = Record<OpinionItemType, number | null>;

  const getScoresFromItems = useCallback((items?: EvaluatorOpinionDto['items']): ScoreMap => {
    const base: ScoreMap = {
      [OpinionItemType.NS]: 0,
      [OpinionItemType.ME]: 0,
      [OpinionItemType.AQ]: 0,
      [OpinionItemType.R]: 0,
    };
    items?.forEach((item) => {
      const type = item.type as OpinionItemType;
      if (type in base) {
        base[type] = typeof item.score === 'number' ? item.score : base[type] ?? 0;
      }
    });
    return base;
  }, []);

  const hasNonZeroMonitoringScores = useCallback((scores: ScoreMap) =>
    [OpinionItemType.NS, OpinionItemType.ME, OpinionItemType.AQ].some(
      (type) => scores[type] !== 0,
    ), []);

  const getLatestMonitoringScores = useCallback((tokenState: TokenState): ScoreMap | null => {
    const monitoringResults = tokenState.monitoringResults || [];
    if (monitoringResults.length === 0) return null;
    const sorted = [...monitoringResults].sort((a, b) => {
      const aTime = a.monitoringSince ? new Date(a.monitoringSince).getTime() : 0;
      const bTime = b.monitoringSince ? new Date(b.monitoringSince).getTime() : 0;
      return bTime - aTime;
    });

    for (const result of sorted) {
      const scores = getScoresFromItems(result.opinion?.items);
      if (!hasNonZeroMonitoringScores(scores)) {
        continue;
      }
      return scores;
    }
    return null;
  }, [getScoresFromItems, hasNonZeroMonitoringScores]);

  const getEvaluationAggregateScores = useCallback(
    (tokenState: TokenState): ScoreMap | null => {
      const evaluation = tokenState.evaluation;
      if (!evaluation?.opinions?.length) {
        return null;
      }
      const aggregate: ScoreMap = {
        [OpinionItemType.NS]: 0,
        [OpinionItemType.ME]: 0,
        [OpinionItemType.AQ]: 0,
        [OpinionItemType.R]: 0,
      };
      evaluation.opinions.forEach((op) => {
        const scores = getScoresFromItems(op.items);
        (Object.keys(aggregate) as OpinionItemType[]).forEach((key) => {
          if (typeof scores[key] === 'number') {
            aggregate[key] = (aggregate[key] ?? 0) + (scores[key] as number);
          }
        });
      });
      return aggregate;
    },
    [getScoresFromItems],
  );

  const getEffectiveScore = useCallback(
    (tokenState: TokenState, type: OpinionItemType): number | null => {
      const latestScores = getLatestMonitoringScores(tokenState);
      if (latestScores && typeof latestScores[type] === 'number') {
        return latestScores[type]! * 3; 
      }
      const aggregated = getEvaluationAggregateScores(tokenState);
      if (aggregated && typeof aggregated[type] === 'number') {
        return aggregated[type];
      }
      return null;
    },
    [getLatestMonitoringScores, getEvaluationAggregateScores],
  );

  // 검색 초기화
  const handleClearSearch = useCallback(() => {
    setSearchKeyword('');
    setAppliedSearchKeyword('');
  }, []);

  const handleClearKeywordSearch = useCallback(() => {
    setKeywordSearchInput('');
    setAppliedKeywordSearchInput('');
  }, []);

  const handleStartMemoEdit = useCallback((tokenState: TokenState) => {
    setEditingMemoAddress(tokenState.tokenAddress);
    setMemoDraft(tokenState.memo || '');
  }, []);

  const handleCancelMemoEdit = useCallback(() => {
    setEditingMemoAddress(null);
    setMemoDraft('');
  }, []);

  const handleSaveMemo = useCallback(async () => {
    if (!editingMemoAddress) return;
    setIsMemoSaving(true);
    try {
      await feManagementSystemApi.updateTokenState(editingMemoAddress, {
        memo: memoDraft.trim() || undefined,
      });
      showToaster('Note saved.', 'success');
      await fetchTokenStates(true);
      setEditingMemoAddress(null);
    } catch (error: any) {
      console.error('메모 저장 실패:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to save note.'),
        'error',
      );
    } finally {
      setIsMemoSaving(false);
    }
  }, [editingMemoAddress, memoDraft, fetchTokenStates, showToaster]);

  // Enter 키로 검색 적용 (통일 API 재조회)
  const handleSearchKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleApplySearch();
    },
    [handleApplySearch]
  );
  const handleKeywordSearchKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleApplySearch();
    },
    [handleApplySearch]
  );

  // 정렬 핸들러 — 정렬 변경 시 API로 해당 정렬 데이터 재요청
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  }, [sortField]);

  // 무한 스크롤: IntersectionObserver + 스크롤 위치 폴백 (정렬/레이아웃 변경 시에도 트리거 안정화)
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const scrollRoot = scrollContainerRef.current;
    if (!scrollRoot) return;

    const tryLoadMore = () => {
      if (loadMoreInFlightRef.current) return;
      const { page: p, hasMore: more, isLoadingMore: loading, fetch: fn } = loadMoreParamsRef.current;
      if (!more || loading) return;
      loadMoreInFlightRef.current = true;
      fn(p + 1, true);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        tryLoadMore();
      },
      { root: scrollRoot, rootMargin: '200px', threshold: 0 }
    );

    if (sentinel && hasMore && !isLoadingMore) {
      observer.observe(sentinel);
    }

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollRoot;
      const gap = scrollHeight - scrollTop - clientHeight;
      if (gap < 150) tryLoadMore();
    };

    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      scrollRoot.removeEventListener('scroll', onScroll);
    };
  }, [hasMore, isLoadingMore, tokenStates.length]);

     // 날짜 포맷팅
    const getLatestMonitoringTotal = useCallback((tokenState: TokenState): number | null => {
      const monitoringResults = tokenState.monitoringResults || [];
      if (monitoringResults.length === 0) return null;
      const sorted = [...monitoringResults].sort((a, b) => {
        const aTime = a.monitoringSince ? new Date(a.monitoringSince).getTime() : 0;
        const bTime = b.monitoringSince ? new Date(b.monitoringSince).getTime() : 0;
        return bTime - aTime;
      });

      const calculateTotal = (items?: EvaluatorOpinionDto['items']) => {
        if (!items) return null;
      const scores = {
        [OpinionItemType.NS]: 0,
        [OpinionItemType.ME]: 0,
        [OpinionItemType.AQ]: 0,
        [OpinionItemType.R]: 0,
      };
        items.forEach((item: EvaluatorOpinionDto['items'][number]) => {
          const type = item.type as OpinionItemType;
          const value = typeof item.score === 'number' ? item.score : scores[type];
          scores[type] = value;
        });

        const hasPositiveScore = [OpinionItemType.NS, OpinionItemType.ME, OpinionItemType.AQ].some(
          (key) => scores[key] !== 0,
        );
        if (!hasPositiveScore) {
          return null;
        }
        return (
          scores[OpinionItemType.NS] +
          scores[OpinionItemType.ME] +
          scores[OpinionItemType.AQ] -
          scores[OpinionItemType.R]
        );
      };

      for (const result of sorted) {
        const total = calculateTotal(result.opinion?.items);
        if (total !== null) {
      return total;
        }
      }

      return null;
    }, []);

    const calculateRawOpinionsTotal = useCallback((tokenState: TokenState): number | null => {
      const evaluation = tokenState.evaluation;
      if (!evaluation?.opinions?.length) {
        return null;
      }
      const jScore = evaluation.opinions.find((op) => op.name === 'J')?.totalScore;
      const hScore = evaluation.opinions.find((op) => op.name === 'H')?.totalScore;
      const sScore = evaluation.opinions.find((op) => op.name === 'S')?.totalScore;
      if (
        typeof jScore === 'number' &&
        typeof hScore === 'number' &&
        typeof sScore === 'number'
      ) {
        return jScore + hScore + sScore;
      }
      return null;
    }, []);

   const getEffectiveTotal = useCallback((tokenState: TokenState): number | null => {
      const latestScore = getLatestMonitoringTotal(tokenState);
      const latestDisplay =
        typeof latestScore === 'number' ? latestScore * 3 : calculateRawOpinionsTotal(tokenState);
      return latestDisplay;
    }, [getLatestMonitoringTotal, calculateRawOpinionsTotal]);

    const getLatestMonitoringComment = useCallback((tokenState: TokenState): string | null => {
      const monitoringResults = tokenState.monitoringResults || [];
      if (monitoringResults.length === 0) {
        return null;
      }

      const sorted = [...monitoringResults].sort((a, b) => {
        const aTime = a.monitoringSince ? new Date(a.monitoringSince).getTime() : 0;
        const bTime = b.monitoringSince ? new Date(b.monitoringSince).getTime() : 0;
        return bTime - aTime;
      });

      for (const result of sorted) {
        if (result.opinion?.comment) {
          const trimmed = result.opinion.comment.trim();
          if (trimmed.length > 0) {
            return trimmed;
          }
        }

        if (result.note) {
          const trimmed = result.note.trim();
          if (trimmed.length > 0) {
            return trimmed;
          }
        }

        const firstItemComment = result.opinion?.items?.map((item) => item.comment?.trim()).find(
          (c) => c && c.length > 0,
        );
        if (firstItemComment) {
          return firstItemComment;
        }
      }

      return null;
    }, []);
      
  // API가 검색/키워드/카테고리/상태 필터를 적용한 데이터를 반환하므로, 여기서는 Top10 표시만 계산
  const filteredAndSortedTokenStates = useMemo(() => {
    const topTenSet = new Set(
      [...tokenStates]
        .map((state) => ({ state, latestTotal: getEffectiveTotal(state) }))
        .filter((entry) => entry.latestTotal !== null)
        .sort((a, b) => (b.latestTotal ?? -Infinity) - (a.latestTotal ?? -Infinity))
        .slice(0, 10)
        .map((entry) => entry.state.tokenAddress),
    );
    return tokenStates.map((tokenState) => ({
      tokenState,
      isTopTen: topTenSet.has(tokenState.tokenAddress),
    }));
  }, [tokenStates, getEffectiveTotal]);

 

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    return formatTimestampToLocaleString(date, 'en-US', {
      timeZone: 'Asia/Seoul',
    });
  };

  // 정렬 아이콘 렌더링
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <svg className="w-3 h-3 ml-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };


  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header title="AF Monitor" showPageControls={false} />

      <main className="max-w-8xl mx-auto p-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <h1 className="text-2xl font-bold text-white">Notes</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRestartFetchingFailedTokens}
            disabled={isRestartingFetchingFailedTokens}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 border border-emerald-500 text-xs font-medium text-white rounded-md hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRestartingFetchingFailedTokens ? '재시작 중...' : 'Restart fetching failed tokens'}
          </button>
        <button
          onClick={() => fetchTokenStates(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800 border border-slate-700 text-xs font-medium text-gray-200 rounded-md hover:bg-slate-700 transition-colors"
        >
           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
             <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
         </svg> 
          Refresh
        </button>
        </div>
      </div>

        {/* 검색 영역: 왼쪽 = Address/Symbol/Name/Memo, 오른쪽 = Keyword */}
        <div className="mb-4 flex gap-2 items-center">
          <div className="flex-1 min-w-0 relative">
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={handleSearchKeyPress}
              placeholder="Token Address, Symbol, Name, Memo..."
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchKeyword && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                aria-label="검색 초기화"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0 relative">
            <input
              type="text"
              value={keywordSearchInput}
              onChange={(e) => setKeywordSearchInput(e.target.value)}
              onKeyDown={handleKeywordSearchKeyPress}
              placeholder="Keywords (comma-separated)"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {keywordSearchInput && (
              <button
                type="button"
                onClick={handleClearKeywordSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                aria-label="키워드 검색 초기화"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleApplySearch}
            disabled={isLoading}
            className="shrink-0 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Loading...' : 'Search'}
          </button>
        </div>

        {/* 검색 적용 시 결과 안내 (Search/Enter로 적용된 경우만) */}
        {(appliedSearchKeyword.trim() || appliedKeywordSearchInput.trim()) && (
          <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg text-blue-200">
            {filteredAndSortedTokenStates.length > 0
              ? `${filteredAndSortedTokenStates.length}개의 토큰을 표시 중입니다.`
              : '검색 조건에 맞는 토큰이 없습니다.'}
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">Loading...</div>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <div
                ref={scrollContainerRef}
                className="overflow-y-auto max-h-[calc(100vh-200px)]"
              >
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10 bg-gray-800">
                    <tr className="border-b border-gray-700">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 w-16"></th>
                      <th className="px-2 py-3 text-left text-sm font-semibold text-gray-300">Token Address</th>
                      <th className="px-2 py-3 text-left text-sm font-semibold text-gray-300">Symbol</th>
                      <th className="px-2 py-3 text-left text-sm font-semibold text-gray-300">Keywords</th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('category')}
                      >
                        <div className="flex items-center justify-center">
                          Category
                          {renderSortIcon('category')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center justify-center">
                          Status
                          {renderSortIcon('status')}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">
                        MC
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">
                        LAF Rank
                      </th>
                     {/* TODO: */}
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('ns_total')}
                      >
                        <div className="flex items-center justify-center">
                          NS
                          {renderSortIcon('ns_total')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('me_total')}
                      >
                        <div className="flex items-center justify-center">
                          ME
                          {renderSortIcon('me_total')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('aq_total')}
                      >
                        <div className="flex items-center justify-center">
                          AQ
                          {renderSortIcon('aq_total')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('r_total')}
                      >
                        <div className="flex items-center justify-center">
                          R&nbsp;
                          {renderSortIcon('r_total')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('j_score')}
                      >
                        <div className="flex items-center justify-center">
                          J&nbsp;
                          {renderSortIcon('j_score')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('h_score')}
                      >
                        <div className="flex items-center justify-center">
                          H&nbsp;
                          {renderSortIcon('h_score')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('s_score')}
                      >
                        <div className="flex items-center justify-center">
                          S&nbsp;
                          {renderSortIcon('s_score')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('total')}
                      >
                        <div className="flex items-center justify-center">
                          Total (Old)
                          {renderSortIcon('total')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('latestTotal')}
                      >
                        <div className="flex items-center justify-center">
                          Total (New)
                          {renderSortIcon('latestTotal')}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 max-w-[280px]">Summary</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 max-w-[280px]">Memo</th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-300 w-1/12 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('retrospective')}
                      >
                        <div className="flex items-center">
                          Retrospective
                          {renderSortIcon('retrospective')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center">
                          Created At
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                        onClick={() => handleSort('updatedAt')}
                      >
                        <div className="flex items-center">
                          Updated At
                          {renderSortIcon('updatedAt')}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                  {tokenStates.length === 0 ? (
                    <tr>
                      <td colSpan={22} className="px-4 py-8 text-center text-gray-400">
                        토큰 상태가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedTokenStates.map(({ tokenState, isTopTen }, idx) => {
                      const liveData = getTokenData(tokenState.tokenAddress);
                      const imageUrl = liveData?.tokenInfo?.imageUrl;
                      
                      const currentMc = liveData?.priceTracker?.mcap ?? liveData?.statistics?.lastFlow.mc;
                      const lafRank = liveData?.ranking?.cumulativeConvictionAtSameTimeRank;

                      // Evaluation 데이터 추출
                      const evaluation = tokenState.evaluation;
                      const summary = evaluation?.summary;
                      const latestMonitoringComment = getLatestMonitoringComment(tokenState);
                      const summaryToDisplay = latestMonitoringComment ?? summary ?? '-';
                      
                      // Note 존재 여부 확인
                      const hasNote = (tokenState.status === TokenStatus.FETCHING_DATA || tokenState.status === TokenStatus.FETCHING_FAILED) ? true : !!tokenState.note?.contents;
                      // opinions를 name을 키로 하는 Map으로 변환
                      const opinionsMap = new Map<string, { totalScore: number }>();
                      evaluation?.opinions?.forEach(op => {
                        opinionsMap.set(op.name, { totalScore: op.totalScore });
                      });
                      
                      const jScore = opinionsMap.get('J')?.totalScore;
                      const hScore = opinionsMap.get('H')?.totalScore;
                      const sScore = opinionsMap.get('S')?.totalScore;
                      const nsScore = getEffectiveScore(tokenState, OpinionItemType.NS);
                      const meScore = getEffectiveScore(tokenState, OpinionItemType.ME);
                      const aqScore = getEffectiveScore(tokenState, OpinionItemType.AQ);
                      const rScore = getEffectiveScore(tokenState, OpinionItemType.R);
                      
                      const latestTotalDisplay = getEffectiveTotal(tokenState);
                      const total = calculateRawOpinionsTotal(tokenState);
                      
                      const rowStripeClass =
                        Math.floor(idx / 10) % 2 === 0 ? 'bg-gray-900/20' : 'bg-gray-800/60';

                      return (
                        <tr
                          key={tokenState.tokenAddress}
                          className={`border-b border-gray-700/50 transition-colors hover:bg-gray-700/50 ${
                            isTopTen ? 'bg-blue-900/30' : rowStripeClass
                          }`}
                        >
                          <td className="px-4 py-3 text-center">
                            <img
                              src={imageUrl || 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQYoWXFc1ZD2ULUuOL8Xtb8V8rizl57AIB3rw&s'}
                              alt={tokenState.tokenSymbol || 'Token'}
                              className="w-8 h-8 rounded-full object-cover mx-auto"
                              onError={(e) => {
                                // 이미지 로드 실패 시 기본 이미지로 대체
                                (e.target as HTMLImageElement).src = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQYoWXFc1ZD2ULUuOL8Xtb8V8rizl57AIB3rw&s';
                              }}
                            />
                          </td>
                          <td className="px-2 py-3 text-sm text-white font-mono">
                            <div className="flex items-center gap-2">
                              <span>{tokenState.tokenAddress.slice(0, 4)}...{tokenState.tokenAddress.slice(-4)}</span>
                              <CopyButton 
                                text={tokenState.tokenAddress} 
                                size="md"
                                title="토큰 주소 복사"
                                onCopy={() => {
                                  showToaster('Token address copied to clipboard!', 'success');
                                }}
                              />
                              <button
                              style={{
                                marginLeft: -5
                              }} 
                                className={`w-8 h-8 rounded-sm flex items-center justify-center transition-opacity ${
                                tokenState.tokenAddress
                                    ? 'opacity-70 hover:opacity-100 cursor-pointer'
                                    : 'opacity-5 cursor-not-allowed'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const gmgnIdentifier = tokenState.tokenAddress;
                                  const gmgnUrl = `http://gmgn.ai/sol/token/solscan_${gmgnIdentifier}`;
                                  if (gmgnIdentifier) {
                                    window.open(gmgnUrl, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                disabled={!tokenState.tokenAddress}
                                title="GMGN"
                              >
                                <div className="w-8 h-8 rounded-sm overflow-hidden flex items-center justify-center">
                                  <img src="/gmgn.png" alt="gmgn" className="w-4 h-4" />
                                </div>
                              </button>
                            </div>
                          </td>
                        <td className="px-2 py-3 text-sm text-gray-300 ">
                          {tokenState.tokenSymbol || 'N/A'}
                        </td>
                        <td className="px-2 py-3 text-sm">
                          <div
                            className="relative"
                            onMouseEnter={() => setHoveredKeywordAddress(tokenState.tokenAddress)}
                            onMouseLeave={() =>
                              setHoveredKeywordAddress((prev) =>
                                prev === tokenState.tokenAddress ? null : prev,
                              )
                            }
                          >
                            {editingKeywordAddress === tokenState.tokenAddress ? (
                              <KeywordEditor
                                tokenAddress={tokenState.tokenAddress}
                                initialKeywords={tokenState.keywords || []}
                                onSuccess={() => {
                                  fetchTokenStates(true);
                                  setEditingKeywordAddress(null);
                                }}
                                onClose={() => setEditingKeywordAddress(null)}
                              />
                            ) : (
                              <>
                                {tokenState.keywords?.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {tokenState.keywords.map((keyword) => (
                                      <KeywordChip
                                        key={`${tokenState.tokenAddress}-kw-${keyword}`}
                                        keyword={keyword}
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-500">-</span>
                                )}
                                {hoveredKeywordAddress === tokenState.tokenAddress && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingKeywordAddress(tokenState.tokenAddress);
                                    }}
                                    className="px-2 py-0.5 text-[11px] rounded bg-gray-700 text-gray-200 hover:bg-gray-600 transition-colors ml-2"
                                  >
                                    Edit
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <CategoryChip category={tokenState.category} />
                        </td>
                          <td className="px-4 py-3 text-sm text-center">
                              <StatusChip
                                tokenAddress={tokenState.tokenAddress}
                                status={tokenState.status as TokenStatus}
                                onSuccess={() => fetchTokenStates(true)}
                              />
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${currentMc && currentMc >= 1000000 ? 'text-orange-400 font-bold' : 'text-gray-300'}`}>
                            {currentMc ? `$${formatNumber(currentMc)}` : '-'}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${lafRank && lafRank < 1 ? 'text-orange-400 font-bold' : 'text-gray-300'}`}>
                            {lafRank ? `${(lafRank).toFixed(2)}%` : '-'}
                          </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300 border-l-2 border-gray-700/50">
                          {typeof nsScore === 'number' ? nsScore : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300">
                          {typeof meScore === 'number' ? meScore : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300">
                          {typeof aqScore === 'number' ? aqScore : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300">
                          {typeof rScore === 'number' ? rScore * -1 : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300 border-l-2 border-gray-700/50">
                          {typeof jScore === 'number' ? jScore : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300">
                          {typeof hScore === 'number' ? hScore : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300 border-r-2 border-gray-700/50">
                          {typeof sScore === 'number' ? sScore : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-300 font-semibold">
                          {total ?? '-'}
                        </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-300 font-semibold border-r-2 border-gray-700/50">
                        {latestTotalDisplay ?? '-'}
                      </td>
                        <td className="px-4 py-3 text-sm text-gray-400 max-w-[340px] align-top">
                          <div className="max-w-[280px] break-all" title={summaryToDisplay}>
                            {summaryToDisplay.length > 80 &&
                             expandedSummaryAddress !== tokenState.tokenAddress ? (
                              <>
                                {summaryToDisplay.slice(0, 80)}
                                {' ... '}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedSummaryAddress(tokenState.tokenAddress);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 underline ml-0.5"
                                >
                                  더보기
                                </button>
                              </>
                            ) : summaryToDisplay.length > 100 &&
                              expandedSummaryAddress === tokenState.tokenAddress ? (
                              <>
                                {summaryToDisplay}
                                {' '}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedSummaryAddress(null);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 underline"
                                >
                                  접기
                                </button>
                              </>
                            ) : (
                              summaryToDisplay
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400 border-r-2 border-gray-700/50 max-w-[280px]">
                          <div
                            className="relative space-y-2 max-w-[340px]"
                            onMouseEnter={() => setHoveredMemoAddress(tokenState.tokenAddress)}
                            onMouseLeave={() =>
                              setHoveredMemoAddress((prev) =>
                                prev === tokenState.tokenAddress ? null : prev,
                              )
                            }
                          >
                            {editingMemoAddress === tokenState.tokenAddress ? (
                              <div className="space-y-2">
                                <textarea
                                  value={memoDraft}
                                  onChange={(e) => setMemoDraft(e.target.value)}
                                  className="w-full min-h-[60px] bg-gray-900 text-white px-3 py-2 rounded border border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                                  placeholder="메모를 입력하세요"
                                />
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={handleSaveMemo}
                                    disabled={isMemoSaving}
                                    className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isMemoSaving ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelMemoEdit}
                                    disabled={isMemoSaving}
                                    className="px-2 py-1 text-xs rounded bg-gray-600 text-white hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between gap-3 overflow-hidden">
                                <span className="text-sm text-gray-400 break-all" title={tokenState.memo || '-'}>
                                  {tokenState.memo || '-'}
                                </span>
                                {hoveredMemoAddress === tokenState.tokenAddress && (
                                  <button
                                    type="button"
                                    onClick={() => handleStartMemoEdit(tokenState)}
                                    className="px-2 py-0.5 text-[11px] rounded bg-gray-700 text-gray-200 hover:bg-gray-600 transition-colors"
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {tokenState.retrospectiveTypes?.length ? (
                            <div className="flex flex-wrap gap-1">
                              {tokenState.retrospectiveTypes.map((type, rtIdx) => (
                                <RetrospectiveTypeChip
                                  key={`${tokenState.tokenAddress}-${type}-${rtIdx}`}
                                  type={type}
                                  className="text-[11px]"
                                />
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {formatDate(tokenState.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {formatDate(tokenState.updatedAt)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => hasNote && handleDetailsClick(tokenState.tokenAddress, tokenState.tokenSymbol)}
                            disabled={!hasNote}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                              hasNote
                                ? 'text-white bg-blue-600 hover:bg-blue-700'
                                : 'text-gray-600 bg-gray-700 cursor-not-allowed opacity-50'
                            }`}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                      );
                    })
                  )}
                  {tokenStates.length > 0 && (hasMore || isLoadingMore) && (
                      <tr ref={loadMoreSentinelRef}>
                        <td colSpan={22} className="px-8 py-8 text-center text-gray-400">
                          {isLoadingMore
                            ? 'Loading more...'
                            : !hasMore
                              ? '마지막 페이지입니다.'
                              : '\u00a0'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default React.memo(NotesPage);
